/**
 * Workspace navigation coordinator.
 *
 * The single point where tab state (Zustand) and URL (React Router) are
 * updated together. Each action function atomically mutates the store AND
 * calls navigate(), preventing feedback loops.
 *
 * Group-aware: navigates the browser URL only for the primary group.
 * Secondary group tabs don't own the URL — they render via matchPath.
 */
import { useCallback } from 'react';

import { useNavigate } from 'react-router-dom';

import { destinationById } from '@shared/navigation/destinations';

import { useSecondaryPaneVisible } from './useSecondaryPaneVisible';
import { tabForPlace } from '../stores/placeKey';
import { useWorkspaceStore, matchingTab } from '../stores/useWorkspaceStore';

import type { PaneGroup } from '../types';

/** Workspace navigation coordinator — the single store + URL sync point. */
export function useWorkspaceNav() {
  const navigate = useNavigate();
  const tabs = useWorkspaceStore(s => s.tabs);
  const primaryActiveTabId = useWorkspaceStore(s => s.primaryActiveTabId);
  const secondaryActiveTabId = useWorkspaceStore(s => s.secondaryActiveTabId);
  const secondaryVisible = useSecondaryPaneVisible();
  const setActiveTabId = useWorkspaceStore(s => s.setActiveTabId);
  const addTab = useWorkspaceStore(s => s.addTab);
  const closeTabInStore = useWorkspaceStore(s => s.closeTab);
  const moveToSecondaryInStore = useWorkspaceStore(s => s.moveToSecondary);
  const setFocusedGroup = useWorkspaceStore(s => s.setFocusedGroup);

  /** Click a tab in a group's tab bar → make it active in that group. */
  const switchToTab = useCallback(
    (group: PaneGroup, tabId: string) => {
      const tab = tabs.find(t => t.id === tabId && t.group === group);
      if (!tab) return;
      setActiveTabId(group, tabId);
      setFocusedGroup(group);
      // Only navigate the URL for primary group tabs.
      if (group === 'primary') {
        navigate(tab.path, { replace: true });
      }
    },
    [tabs, setActiveTabId, setFocusedGroup, navigate]
  );

  /**
   * The Go box and uncatalogued rail rows: show `path` in the primary pane.
   *
   * One tab per place (`placeKey`): a tab already holding the place is focused
   * and sent to `path`, so a second search reuses the Search tab with the new
   * query. Only an unheld place mints a tab. There is no visible strip to close
   * a duplicate from, so none is ever made.
   */
  const openPath = useCallback(
    (title: string, path: string) => {
      setFocusedGroup('primary');
      const held = tabForPlace(tabs, primaryActiveTabId, path);
      if (held) {
        setActiveTabId('primary', held.id);
        // Already there → the URL is already right; a redundant replace() would
        // drop location.state, as in openDestination.
        if (held.id !== primaryActiveTabId || held.path !== path) {
          navigate(path, { replace: true });
        }
        return;
      }
      const id = `tab-${Date.now()}`;
      addTab({ id, title, path, pinned: false, group: 'primary' });
      setActiveTabId('primary', id);
      navigate(path, { replace: true });
    },
    [tabs, primaryActiveTabId, setActiveTabId, setFocusedGroup, addTab, navigate]
  );

  /**
   * Open-or-focus a catalog destination. The rail, All areas and the Go box all
   * arrive here. Never overwrites the active tab — that is what makes the tab
   * strip trustworthy as memory.
   *
   * Search order: primary group first, then the secondary group only while its
   * pane is visible. A match in a hidden pane counts as no match. No match → a
   * new primary tab. Matching is by destinationId, so Sent never focuses Inbox,
   * and an unknown id (or an uncatalogued tab's missing id) matches nothing.
   */
  const openDestination = useCallback(
    (destinationId: string) => {
      const dest = destinationById(destinationId);
      if (!dest) return;

      const primary = matchingTab(tabs, 'primary', primaryActiveTabId, destinationId);
      if (primary) {
        setActiveTabId('primary', primary.id);
        setFocusedGroup('primary');
        // Already the active tab → the URL is already right; a redundant replace()
        // would drop location.state (journal deep links, hub back-links).
        if (primary.id !== primaryActiveTabId) navigate(primary.path, { replace: true });
        return;
      }

      const secondary = secondaryVisible
        ? matchingTab(tabs, 'secondary', secondaryActiveTabId, destinationId)
        : undefined;
      if (secondary) {
        setActiveTabId('secondary', secondary.id);
        setFocusedGroup('secondary');
        return;
      }

      const id = `tab-${Date.now()}`;
      addTab({ id, title: dest.label, path: dest.path, pinned: false, group: 'primary' });
      setActiveTabId('primary', id);
      setFocusedGroup('primary');
      navigate(dest.path, { replace: true });
    },
    [
      tabs,
      primaryActiveTabId,
      secondaryActiveTabId,
      secondaryVisible,
      setActiveTabId,
      setFocusedGroup,
      addTab,
      navigate,
    ]
  );

  /**
   * Close a tab. If it was active in the primary group, navigate to the
   * neighbor. Secondary group navigation is handled by the pane re-render.
   */
  const closeTab = useCallback(
    (tabId: string) => {
      const tab = tabs.find(t => t.id === tabId);
      if (!tab) return;
      const wasActivePrimary = tab.group === 'primary' && tabId === primaryActiveTabId;
      closeTabInStore(tabId);
      if (wasActivePrimary) {
        const newActiveId = useWorkspaceStore.getState().primaryActiveTabId;
        const newActive = useWorkspaceStore.getState().tabs.find(t => t.id === newActiveId);
        if (newActive) navigate(newActive.path, { replace: true });
      }
    },
    [tabs, primaryActiveTabId, closeTabInStore, navigate]
  );

  /**
   * Right-click → "Open in side pane": move the tab across, then point the URL
   * at whatever is now active in the primary group.
   *
   * The navigate is the fix, not a nicety. `WorkspaceUrlSync` mirrors the URL onto
   * the primary group's active tab whenever either changes, so moving the
   * ACTIVE tab out while the browser still sits on its path rewrites the
   * neighbour that inherits focus into a copy of the moved tab — destroying
   * whatever that neighbour was showing. Going through this coordinator rather
   * than the raw store action is what keeps state and URL in step; `closeTab`
   * has always done the same thing for the same reason.
   */
  const moveToSidePane = useCallback(
    (tabId: string) => {
      moveToSecondaryInStore(tabId);
      const { tabs: nextTabs, primaryActiveTabId: nextActiveId } = useWorkspaceStore.getState();
      // Unchanged means nothing moved (the last primary tab is held back) or a
      // background tab moved — either way the URL already matches.
      if (nextActiveId === primaryActiveTabId) return;
      const nextActive = nextTabs.find(t => t.id === nextActiveId);
      if (nextActive) navigate(nextActive.path, { replace: true });
    },
    [moveToSecondaryInStore, primaryActiveTabId, navigate]
  );

  return {
    switchToTab,
    openPath,
    openDestination,
    closeTab,
    moveToSidePane,
  };
}
